'use client';

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Shortcut {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  label: string;
  items: Shortcut[];
}

export const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    label: 'Navigation',
    items: [
      { keys: ['g', 'd'], description: 'Go to Dashboard' },
      { keys: ['g', 'r'], description: 'Go to Research (current symbol)' },
      { keys: ['g', 'm'], description: 'Go to Market Data' },
      { keys: ['Esc'], description: 'Back to Dashboard' },
    ],
  },
  {
    label: 'Search',
    items: [
      { keys: ['/'], description: 'Focus search' },
      { keys: ['⌘', 'K'], description: 'Focus search' },
      { keys: ['↑', '↓'], description: 'Move highlight in dropdown' },
      { keys: ['Enter'], description: 'Add highlighted result, or open Research' },
    ],
  },
  {
    label: 'Watchlist',
    items: [
      { keys: ['['], description: 'Previous symbol' },
      { keys: [']'], description: 'Next symbol' },
    ],
  },
  {
    label: 'Research view',
    items: [
      { keys: ['1'], description: 'Overview tab' },
      { keys: ['2'], description: 'Financials tab' },
      { keys: ['3'], description: 'Risk tab' },
    ],
  },
  {
    label: 'Market Data view',
    items: [
      { keys: ['1'], description: 'S&P 500' },
      { keys: ['2'], description: 'Dow Jones' },
      { keys: ['3'], description: 'NASDAQ 100' },
      { keys: ['4'], description: 'Russell 2000' },
      { keys: ['5'], description: 'Commodities' },
      { keys: ['6'], description: 'VIX' },
      { keys: ['7'], description: 'Macro' },
    ],
  },
  {
    label: 'Help',
    items: [
      { keys: ['?'], description: 'Show this dialog' },
    ],
  },
];

function Key({ k }: { k: string }) {
  return (
    <kbd
      className="font-mono text-[11px] font-semibold px-1.5 py-0.5 rounded border min-w-[20px] text-center inline-block"
      style={{
        backgroundColor: '#1A2035',
        borderColor: '#1F2937',
        color: '#E5E7EB',
      }}
    >
      {k}
    </kbd>
  );
}

export function ShortcutsHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    // Save the previously focused element so we can restore on close.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Focus the close button on next tick (after the dialog mounts).
    requestAnimationFrame(() => closeButtonRef.current?.focus());

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === '?') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      // Trap Tab focus inside the dialog.
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        ).filter((el) => !el.hasAttribute('disabled'));
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    // Capture phase so we beat the global terminal shortcut listener
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-lg border overflow-hidden shadow-xl"
        style={{ backgroundColor: '#0D1117', borderColor: '#1F2937' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: '#1F2937', backgroundColor: '#111827' }}
        >
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-bold tracking-wider text-[var(--color-accent)]">
              KEYBOARD SHORTCUTS
            </span>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#E5E7EB] transition-colors focus:outline-none focus:text-[var(--color-accent)]"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 max-h-[70vh] overflow-y-auto">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="text-[10px] font-mono font-semibold uppercase tracking-wider mb-2 text-[var(--color-accent)]">
                {group.label}
              </div>
              <ul className="space-y-1.5">
                {group.items.map((s, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-xs">
                    <span style={{ color: '#9CA3AF' }}>{s.description}</span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      {s.keys.map((k, j) => (
                        <span key={j} className="flex items-center gap-1">
                          {j > 0 && <span style={{ color: '#6B7280', fontSize: 10 }}>then</span>}
                          <Key k={k} />
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-t text-[10px] font-mono"
          style={{ borderColor: '#1F2937', backgroundColor: '#111827', color: '#6B7280' }}
        >
          <span>
            Press <Key k="?" /> any time to reopen. Shortcuts are disabled while typing in inputs.
          </span>
          <a
            href="/learn/terminal"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[var(--color-accent)] transition-colors flex-shrink-0"
          >
            Full guide →
          </a>
        </div>
      </div>
    </div>
  );
}
